import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { BACK_TO_SCHOOL_HOMEPAGE_2026_FEATURE_FLAG } from "../../../shared/featureFlags";
import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import ArtShowcaseBanner from "./ArtShowcaseBanner";
import {
  BackToSchoolOfferBanner,
  BTS_PROMO_CODE,
} from "./BackToSchoolOfferBanner";
import MembershipsBannerSwitcher, {
  useShouldShowMembershipBannerByPath,
} from "./MembershipsBannerSwitcher";

/**
 * Gate on top of the MembershipsBannerSwitcher to short-circuit the banner rendering for logged-out users.
 * This helps prevent layout shift and excessive network requests for logged-out users, who can never see the
 * other banners that MembershipsBannerSwitcher renders. This component duplicates some logic from
 * MembershipsBannerSwitcher, so update both if you change the logic here.
 */
export default function MembershipsBannerGate() {
  const { t } = useTranslation(
    "client\\components\\banners\\MembershipsBannerGate",
  );
  const { isLoggedIn, currentUserIsLoading, currentUser } = useSession();
  const { shouldShowBannersToday } = useBannerDismissal();
  const shouldShowBannerByPath = useShouldShowMembershipBannerByPath();
  const isBackToSchoolHomepageEnabled = useFeatureFlag(
    BACK_TO_SCHOOL_HOMEPAGE_2026_FEATURE_FLAG,
  );

  // Gate everything until the user is loaded
  if (currentUserIsLoading) {
    return null;
  }

  if (!isLoggedIn) {
    if (shouldShowBannersToday && shouldShowBannerByPath) {
      if (isBackToSchoolHomepageEnabled) {
        return (
          <BackToSchoolOfferBanner
            config={{
              copy: t("$20 off your first class"),
              code: BTS_PROMO_CODE,
            }}
          />
        );
      }

      return <ArtShowcaseBanner />;
    }

    return null;
  }

  return <MembershipsBannerSwitcher currentUser={currentUser} />;
}
